import { useEffect, useState, useMemo } from "react";
import "./SynonymsManager.css";

const API = "/api/meili";

export default function SynonymsManager() {
  const [loading, setLoading] = useState(false);
  const [syn, setSyn] = useState({});       // mapa completo { base: [a,b,c] }
  const [error, setError] = useState("");

  const [base, setBase] = useState("");     // palabra base para sugerencias
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]); // [{word,count,source:[]}]
  const [selected, setSelected] = useState(new Set());

  // carga inicial
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const r = await fetch(`${API}/synonyms_get.php`);
        if (!r.ok) throw new Error("HTTP " + r.status);
        const data = await r.json();
        setSyn(data || {});
      } catch (e) {
        console.error("GET synonyms:", e);
        setError("No pude cargar los sinónimos");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const countTotal = useMemo(() => {
    return Object.values(syn).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0);
  }, [syn]);

  async function refreshAll() {
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/synonyms_get.php`);
      if (!r.ok) throw new Error("HTTP " + r.status);
      const data = await r.json();
      setSyn(data || {});
    } catch (e) {
      setError("No pude recargar los sinónimos");
    } finally {
      setLoading(false);
    }
  }

  // Sugerir
  async function handleSuggest() {
    if (!base.trim()) {
      alert("Escribe una palabra base (por ejemplo, 'hamburguesa').");
      return;
    }
    setSuggesting(true);
    setError("");
    setSuggestions([]);
    setSelected(new Set());
    try {
      const r = await fetch(`${API}/synonyms_suggest.php?q=${encodeURIComponent(base)}`);
      if (!r.ok) throw new Error("HTTP " + r.status);
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "Error en sugerencias");
      setSuggestions(data.suggestions || []);
      // preseleccionar nada
      setSelected(new Set());
    } catch (e) {
      console.error("suggest:", e);
      setError("No pude obtener sugerencias.");
    } finally {
      setSuggesting(false);
    }
  }

  function toggleWord(w) {
    const s = new Set(selected);
    if (s.has(w)) s.delete(w);
    else s.add(w);
    setSelected(s);
  }

  async function addSelected() {
    const arr = Array.from(selected);
    if (!base.trim() || arr.length === 0) {
      alert("Selecciona una palabra base y al menos un sinónimo sugerido.");
      return;
    }
    // fusionar con lo actual del base
    const current = Array.isArray(syn[base]) ? syn[base] : [];
    const merged = Array.from(new Set([...current, ...arr]));

    try {
      const r = await fetch(`${API}/synonyms_upsert.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ synonyms: { [base]: merged } }),
      });
      const data = await r.json();
      if (data.status !== "enqueued") throw new Error("No encolado");
      // actualizar local sin esperar a tarea
      setSyn(prev => ({ ...prev, [base]: merged }));
      alert(`Sinónimos de "${base}" actualizados (${merged.length}).`);
      setSelected(new Set());
    } catch (e) {
      console.error("upsert:", e);
      alert("No pude guardar los sinónimos.");
    }
  }

  async function deleteKey(k) {
    if (!window.confirm(`Eliminar la clave de sinónimos "${k}"?`)) return;
    try {
      const r = await fetch(`${API}/synonyms_delete.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ words: [k] }),
      });
      const data = await r.json();
      if (data.status !== "enqueued") throw new Error();
      const cp = { ...syn };
      delete cp[k];
      setSyn(cp);
    } catch (e) {
      alert("No pude borrar la clave.");
    }
  }

  function exportJSON() {
    const blob = new Blob([JSON.stringify(syn, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `meili-synonyms-${new Date().toISOString().slice(0,19)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="syn-wrapper">
      <div className="syn-header">
        <h1>Gestión de sinónimos (Meili)</h1>
        <div className="syn-actions">
          <button onClick={refreshAll} disabled={loading}>Refrescar</button>
          <button onClick={exportJSON} disabled={loading}>Exportar JSON</button>
        </div>
      </div>

      <div className="syn-counters">
        <div className="badge"><span>Claves</span><strong>{Object.keys(syn).length}</strong></div>
        <div className="badge"><span>Total valores</span><strong>{countTotal}</strong></div>
      </div>

      <div className="syn-suggest">
        <h3>Sugerir sinónimos a partir de resultados</h3>
        <div className="row">
          <input
            type="text"
            placeholder="Palabra base (ej. hamburguesa)"
            value={base}
            onChange={e => setBase(e.target.value)}
          />
          <button onClick={handleSuggest} disabled={suggesting || !base.trim()}>
            {suggesting ? "Buscando..." : "Sugerir"}
          </button>
          <button onClick={addSelected} disabled={selected.size === 0 || !base.trim()}>
            Añadir seleccionados
          </button>
        </div>

        {suggestions.length > 0 && (
          <div className="sugg-grid">
            {suggestions.map(sg => (
              <div
                key={sg.word}
                className={`sugg-chip ${selected.has(sg.word) ? 'on' : ''}`}
                onClick={() => toggleWord(sg.word)}
                title={`Frecuencia: ${sg.count} · Fuente: ${sg.source.join(', ')}`}
              >
                <span className="w">{sg.word}</span>
                <span className="meta">×{sg.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <div className="syn-error">{error}</div>}

      <div className="syn-list">
        {Object.keys(syn).length === 0 ? (
          <p>No hay sinónimos configurados.</p>
        ) : (
          Object.entries(syn).map(([k, arr]) => (
            <div key={k} className="syn-item">
              <div className="syn-key">
                <strong>{k}</strong>
                <button className="danger" onClick={() => deleteKey(k)}>Borrar clave</button>
              </div>
              <div className="syn-values">
                {arr.map(v => <span key={v} className="pill">{v}</span>)}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
